<template>
    <!-- the scoresheet of a played match, to look at or to save as an image -->
    <v-dialog :value="value" max-width="1200" scrollable @input="$emit('input', $event)">
        <v-card lang="el">
            <v-card-title class="match-sheet-dialog__title">
                <v-icon left>mdi-clipboard-text-outline</v-icon>
                Φύλλο αγώνα
                <v-spacer />
                <v-btn color="primary" depressed small :loading="saving || loadingLogos" :disabled="!sheet" @click="download">
                    <v-icon left small>mdi-download</v-icon>Λήψη εικόνας
                </v-btn>
                <v-btn icon small class="ml-2" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="match-sheet-dialog__body">
                <div v-if="loading" class="match-sheet-dialog__state"><v-progress-circular indeterminate color="primary" /></div>
                <div v-else-if="error" class="match-sheet-dialog__state red--text">{{ error }}</div>

                <!-- what goes into the image: plain blocks, no outside images or web fonts -->
                <div v-else-if="sheet" ref="sheet" class="match-sheet" lang="el">
                    <header class="match-sheet__head">
                        <!-- the logo of the league: a bundled image, so it goes into the image too -->
                        <img :src="logo" alt="" class="match-sheet__logo">
                        <div class="match-sheet__title">
                            <div class="match-sheet__kicker">Commercial League · Φύλλο αγώνα</div>
                            <div class="match-sheet__competition">{{ sheet.competition }}</div>
                            <div class="match-sheet__meta">{{ sheet.round }} · {{ sheet.date }}<span v-if="sheet.arena"> · {{ sheet.arena }}</span></div>
                        </div>
                        <div class="match-sheet__no">Αγώνας<br><b>#{{ sheet.id }}</b></div>
                    </header>

                    <section class="match-sheet__score">
                        <div v-for="(team, index) in sheet.teams" :key="team.id" class="match-sheet__side" :class="{ 'match-sheet__side--away': index === 1, 'match-sheet__side--won': team.won }">
                            <img v-if="team.logo" :src="team.logo" alt="" class="match-sheet__badge">
                            <span v-else class="match-sheet__swatch" :style="{ background: team.color }">{{ team.letter }}</span>
                            <span class="match-sheet__team">{{ team.name }}</span>
                            <span class="match-sheet__points">{{ team.score }}</span>
                        </div>
                    </section>

                    <!-- the score and the team fouls per period -->
                    <table v-if="sheet.periods.length" class="match-sheet__grid match-sheet__periods">
                        <thead>
                            <tr>
                                <th class="match-sheet__left">Ομάδα</th>
                                <th v-for="period in sheet.periods" :key="period">{{ periodLabel(period) }}</th>
                                <th class="match-sheet__strong">Τελικό</th>
                                <th v-if="sheet.hasTeamFouls" class="match-sheet__gap" />
                                <th v-for="period in (sheet.hasTeamFouls ? sheet.periods : [])" :key="`f${period}`">Φ {{ periodLabel(period) }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="team in sheet.teams" :key="team.id">
                                <td class="match-sheet__left"><i class="match-sheet__dot" :style="{ background: team.color }" />{{ team.name }}</td>
                                <td v-for="period in sheet.periods" :key="period">{{ team.periodScore[period] || 0 }}</td>
                                <td class="match-sheet__strong">{{ team.score }}</td>
                                <td v-if="sheet.hasTeamFouls" class="match-sheet__gap" />
                                <td v-for="period in (sheet.hasTeamFouls ? sheet.periods : [])" :key="`f${period}`" :class="{ 'match-sheet__bonus': (team.teamFouls[period] || 0) >= 5 }">{{ team.teamFouls[period] || 0 }}</td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- one box score per team -->
                    <section v-for="team in sheet.teams" :key="`box${team.id}`" class="match-sheet__box">
                        <div class="match-sheet__box-head" :style="{ borderColor: team.color }">
                            <span>{{ team.name }}</span>
                            <span v-if="team.coach" class="match-sheet__coach">Προπονητής: <b>{{ team.coach }}</b></span>
                            <!-- the technical fouls of the coach (C) and of the bench (B): not team fouls -->
                            <span v-if="team.benchTechnicals.coach || team.benchTechnicals.bench" class="match-sheet__coach">
                                Τεχνικές πάγκου: <b>C {{ team.benchTechnicals.coach }} · B {{ team.benchTechnicals.bench }}</b>
                            </span>
                        </div>
                        <table class="match-sheet__grid">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th class="match-sheet__left match-sheet__name">Παίκτης</th>
                                    <th v-if="sheet.hasMinutes">MIN</th>
                                    <th class="match-sheet__strong">ΠΟΝ</th>
                                    <th>2Π</th>
                                    <th>3Π</th>
                                    <th>ΒΟΛ</th>
                                    <th>ΕΠ/ΑΜ</th>
                                    <th>ΡΙΜ</th>
                                    <th>ΑΣΤ</th>
                                    <th>ΚΛ</th>
                                    <th>ΚΟΦ</th>
                                    <th>ΛΑΘ</th>
                                    <th>Φάουλ</th>
                                    <th>PIR</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="row in team.rows" :key="row.id">
                                    <td class="match-sheet__jersey">{{ row.jersey }}</td>
                                    <td class="match-sheet__left match-sheet__name">{{ row.name }}<span v-if="row.mvp" class="match-sheet__mvp">MVP</span></td>
                                    <td v-if="sheet.hasMinutes">{{ row.minutes }}</td>
                                    <td class="match-sheet__strong">{{ row.points }}</td>
                                    <td>{{ row.two }}</td>
                                    <td>{{ row.three }}</td>
                                    <td>{{ row.ft }}</td>
                                    <td>{{ row.offDef }}</td>
                                    <td>{{ row.rebounds }}</td>
                                    <td>{{ row.assists }}</td>
                                    <td>{{ row.steals }}</td>
                                    <td>{{ row.blocks }}</td>
                                    <td>{{ row.turnovers }}</td>
                                    <td>
                                        <!-- the five fouls of a player: P personal, T technical, DI disruptive, FL flagrant (U
                                             unsportsmanlike before FIBA 2026); the ones towards a game disqualification
                                             in a circle, as on the scoresheet of FIBA -->
                                        <span class="match-sheet__fouls">
                                            <i v-for="(foul, index) in row.fouls" :key="index" :class="{ 'match-sheet__foul--on': foul.mark, 'match-sheet__foul--out': row.fouledOut && foul.mark, 'match-sheet__foul--dq': foul.dq }">{{ foul.mark }}</i>
                                        </span>
                                    </td>
                                    <td>{{ row.pir }}</td>
                                </tr>
                                <tr v-if="!team.rows.length">
                                    <td :colspan="sheet.hasMinutes ? 15 : 14" class="match-sheet__empty">Δεν υπάρχουν στατιστικά για την ομάδα</td>
                                </tr>
                            </tbody>
                            <tfoot v-if="team.rows.length">
                                <tr>
                                    <td />
                                    <td class="match-sheet__left match-sheet__name">Σύνολο</td>
                                    <td v-if="sheet.hasMinutes" />
                                    <td class="match-sheet__strong">{{ team.totals.points }}</td>
                                    <td>{{ team.totals.two }}</td>
                                    <td>{{ team.totals.three }}</td>
                                    <td>{{ team.totals.ft }}</td>
                                    <td>{{ team.totals.offDef }}</td>
                                    <td>{{ team.totals.rebounds }}</td>
                                    <td>{{ team.totals.assists }}</td>
                                    <td>{{ team.totals.steals }}</td>
                                    <td>{{ team.totals.blocks }}</td>
                                    <td>{{ team.totals.turnovers }}</td>
                                    <td>{{ team.totals.fouls }}</td>
                                    <td>{{ team.totals.pir }}</td>
                                </tr>
                                <tr class="match-sheet__pcts">
                                    <td />
                                    <td class="match-sheet__left match-sheet__name">Ποσοστά</td>
                                    <td v-if="sheet.hasMinutes" />
                                    <td />
                                    <td>{{ team.totals.twoPct }}</td>
                                    <td>{{ team.totals.threePct }}</td>
                                    <td>{{ team.totals.ftPct }}</td>
                                    <td colspan="8" />
                                </tr>
                            </tfoot>
                        </table>
                    </section>

                    <footer class="match-sheet__foot">
                        <div class="match-sheet__officials">
                            <div>
                                <span class="match-sheet__label">Διαιτητές</span>
                                <b>{{ sheet.referees || '–' }}</b>
                            </div>
                            <div v-if="sheet.staff">
                                <span class="match-sheet__label">Γραμματεία</span>
                                <b>{{ sheet.staff }}</b>
                            </div>
                            <div v-if="sheet.mvp">
                                <span class="match-sheet__label">MVP</span>
                                <b>{{ sheet.mvp }}</b>
                            </div>
                        </div>
                        <div class="match-sheet__signs">
                            <span>Υπογραφή διαιτητή</span>
                            <span>Υπογραφή γραμματέα</span>
                        </div>
                        <div class="match-sheet__legend">
                            2Π / 3Π / ΒΟΛ: εύστοχα-προσπάθειες · ΕΠ/ΑΜ: επιθετικά/αμυντικά ριμπάουντ · Φάουλ: P προσωπικό, T τεχνικό, U αντιαθλητικό · Φ: ομαδικά φάουλ περιόδου
                        </div>
                    </footer>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import { toPng } from 'html-to-image'
    import matchService from '@/services/match'

    const num = value => Number(value) || 0
    // the images of the storage come through the public API, which sends the CORS headers the storage does not:
    // as data: urls they go into the saved picture too
    const WEB_API_URL = String(process.env.VUE_APP_API_HOST_DOMAIN || '').replace(/\/api\/cms\/?$/, '/api/web')
    const STORAGE_URL = 'https://storage.googleapis.com/'
    const storageDataUrl = path => {
        if (!path) return Promise.resolve(null)
        const storagePath = /^https?:\/\//i.test(path) ? (path.startsWith(STORAGE_URL) ? decodeURIComponent(path.slice(STORAGE_URL.length)) : null) : path
        if (!storagePath) return Promise.resolve(null)
        return fetch(`${WEB_API_URL}/assets?path=${encodeURIComponent(storagePath)}`)
            .then(resp => (resp.ok ? resp.blob() : null))
            .then(blob => (blob ? new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = () => resolve(reader.result)
                reader.onerror = () => reject(reader.error)
                reader.readAsDataURL(blob)
            }) : null))
            .catch(() => null)
    }
    const pct = (made, att) => att ? `${Math.round(made / att * 100)}%` : '–'
    const ratio = (made, att) => `${made}-${att}`
    const PALETTE = ['#232323', '#037ade', '#03b58f', '#e5533d', '#7a4fd6', '#d99a00']

    // every foul of a player, all of them team fouls (the unsportsmanlike one before FIBA 2026, the disruptive and
    // the flagrant one after)
    const FOUL_KEYS = ['personalFouls', 'technicalFouls', 'unsportsmanlikeFouls', 'disruptiveFouls', 'flagrantFouls']

    export default {
        name: 'MatchSheet',
        props: {
            // the dialog is open
            value: {
                type: Boolean,
                default: false
            },
            matchId: {
                type: [Number, String],
                default: null
            }
        },
        data() {
            return {
                loading: false,
                saving: false,
                loadingLogos: false,
                error: null,
                sheet: null,
                logo: require('@/assets/main-logo.png')
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(open) {
                    if (open && this.matchId) this.load()
                }
            },
            matchId() {
                if (this.value && this.matchId) this.load()
            }
        },
        methods: {
            load() {
                const id = this.matchId
                this.loading = true
                this.error = null
                this.sheet = null
                Promise.all([
                    matchService.getMatch(id),
                    matchService.getMatchStats(id),
                    // the feed gives the team fouls per period; a match imported from a CSV has none
                    matchService.getMatchLogs(id).catch(() => ({ data: [] }))
                ]).then(([match, stats, logs]) => {
                    if (id !== this.matchId) return
                    this.sheet = this.build(match.data, stats.data, logs.data || [])
                    this.loadLogos(this.sheet, [stats.data.team1, stats.data.team2])
                }).catch(() => {
                    this.error = 'Δεν ήταν δυνατή η φόρτωση του αγώνα.'
                }).finally(() => {
                    this.loading = false
                })
            },
            // the logos of the teams, in place of their letters once they come
            loadLogos(sheet, sides) {
                this.loadingLogos = true
                Promise.all(sheet.teams.map((team, index) => storageDataUrl((sides[index] || {}).logo).then(logo => { team.logo = logo })))
                    .finally(() => { if (this.sheet === sheet) this.loadingLogos = false })
            },
            build(match, stats, logs) {
                const sides = [stats.team1, stats.team2]
                const scores = [num(stats.team1Score), num(stats.team2Score)]
                const periodSet = new Set([1, 2, 3, 4])
                const teams = sides.map((team, index) => this.buildTeam(team || {}, index, scores, logs, periodSet))
                const periods = [...periodSet].sort((a, b) => a - b)
                const anyPeriods = teams.some(team => Object.keys(team.periodScore).length)

                // the MVP: the best PIR of the winner, as for the social media post
                let mvp = null
                if (scores[0] !== scores[1]) {
                    const winner = teams[scores[0] > scores[1] ? 0 : 1]
                    const best = winner.rows.reduce((top, row) => (!top || row.pir > top.pir ? row : top), null)
                    if (best) {
                        best.mvp = true
                        mvp = `#${best.jersey} ${best.name} (${winner.name}) · ${best.points} π. · PIR ${best.pir}`
                    }
                }

                const competition = stats.cupLeague ? stats.cupLeague.title : (stats.league ? stats.league.title : 'Φιλικός')
                return {
                    id: match.id,
                    competition,
                    round: match.customRoundText || (match.round ? `Αγωνιστική ${match.round}` : (match.phase || '')),
                    date: momentTimezone(match.matchDatetime).tz('Europe/Athens').format('DD/MM/YYYY · HH:mm'),
                    arena: match.arena ? match.arena.name : '',
                    referees: (match.referees || []).map(referee => referee.refereeName).filter(Boolean).join(', '),
                    staff: (match.staff || []).map(person => person.staffName).filter(Boolean).join(', '),
                    teams,
                    periods: anyPeriods ? periods : [],
                    hasTeamFouls: logs.length > 0,
                    hasMinutes: teams.some(team => team.rows.some(row => row.seconds > 0)),
                    mvp
                }
            },
            buildTeam(team, index, scores, logs, periodSet) {
                const players = [...(team.players || [])]
                const coach = players.find(player => player.coach)
                const rows = players
                    .filter(player => player.stats && player.stats.length)
                    .map(player => this.buildRow(player, player.stats[0]))
                    .sort((a, b) => (Number(a.jersey) || 999) - (Number(b.jersey) || 999))

                const periodScore = {}
                players.forEach(player => {
                    const stat = player.stats && player.stats[0]
                    if (!stat || !stat.periodScore) return
                    Object.keys(stat.periodScore).forEach(key => {
                        const period = Number(key)
                        // the periods count from 1; a 0 is an empty leftover
                        if (!(period >= 1)) return
                        periodSet.add(period)
                        periodScore[period] = (periodScore[period] || 0) + num(stat.periodScore[key])
                    })
                })

                const teamFouls = {}
                // the technical fouls of the coach and of the bench: on the team, apart from the team fouls
                const benchTechnicals = { coach: 0, bench: 0 }
                logs.filter(log => Number(log.teamId) === Number(team.id)).forEach(log => {
                    benchTechnicals.coach += num(log.coachTechnicalFouls)
                    benchTechnicals.bench += num(log.benchTechnicalFouls)
                    const fouls = FOUL_KEYS.reduce((total, key) => total + num(log[key]), 0)
                    if (fouls && log.period >= 1) teamFouls[log.period] = (teamFouls[log.period] || 0) + fouls
                })

                const sum = key => rows.reduce((total, row) => total + row.raw[key], 0)
                const made2 = sum('twoPointsMade')
                const att2 = sum('twoPointAttempts')
                const made3 = sum('threePointsMade')
                const att3 = sum('threePointAttempts')
                const madeFt = sum('freeThrowsMade')
                const attFt = sum('freeThrowAttempts')
                const name = team.name || `Ομάδα ${index + 1}`
                return {
                    id: team.id || index,
                    name,
                    letter: name.trim().charAt(0).toUpperCase(),
                    logo: null,
                    color: this.teamColor(team, index),
                    score: scores[index],
                    won: scores[index] > scores[1 - index],
                    coach: coach ? [coach.name, coach.surname].filter(Boolean).join(' ') : '',
                    rows,
                    periodScore,
                    teamFouls,
                    benchTechnicals,
                    totals: {
                        points: sum('points'),
                        two: ratio(made2, att2),
                        three: ratio(made3, att3),
                        ft: ratio(madeFt, attFt),
                        twoPct: pct(made2, att2),
                        threePct: pct(made3, att3),
                        ftPct: pct(madeFt, attFt),
                        offDef: `${sum('offensiveRebounds')}/${sum('defensiveRebounds')}`,
                        rebounds: sum('rebounds'),
                        assists: sum('assists'),
                        steals: sum('steals'),
                        blocks: sum('blockedShots'),
                        turnovers: sum('turnovers'),
                        fouls: FOUL_KEYS.reduce((total, key) => total + sum(key), 0),
                        pir: sum('pir')
                    }
                }
            },
            buildRow(player, stat) {
                const keys = ['points', 'twoPointsMade', 'twoPointAttempts', 'threePointsMade', 'threePointAttempts', 'freeThrowsMade', 'freeThrowAttempts',
                              'offensiveRebounds', 'defensiveRebounds', 'rebounds', 'assists', 'steals', 'blockedShots', 'turnovers',
                              ...FOUL_KEYS, 'category2TechnicalFouls', 'pir']
                const raw = {}
                keys.forEach(key => { raw[key] = num(stat[key]) })
                const marks = (count, mark, dq) => Array.from({ length: count }, () => ({ mark, dq }))
                const category2 = Math.min(raw.category2TechnicalFouls, raw.technicalFouls)
                const fouls = [
                    ...marks(raw.personalFouls, 'P', false),
                    ...marks(raw.technicalFouls - category2, 'T', true),
                    ...marks(category2, 'T', false),
                    ...marks(raw.unsportsmanlikeFouls, 'U', true),
                    ...marks(raw.disruptiveFouls, 'DI', false),
                    ...marks(raw.flagrantFouls, 'FL', true)
                ]
                const seconds = num(stat.playTimeInSecs)
                return {
                    id: player.id,
                    jersey: player.alternativeJersey || stat.jersey || player.jersey || '',
                    name: [player.name, player.surname].filter(Boolean).join(' '),
                    raw,
                    seconds,
                    minutes: seconds ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` : '–',
                    points: raw.points,
                    two: ratio(raw.twoPointsMade, raw.twoPointAttempts),
                    three: ratio(raw.threePointsMade, raw.threePointAttempts),
                    ft: ratio(raw.freeThrowsMade, raw.freeThrowAttempts),
                    offDef: `${raw.offensiveRebounds}/${raw.defensiveRebounds}`,
                    rebounds: raw.rebounds,
                    assists: raw.assists,
                    steals: raw.steals,
                    blocks: raw.blockedShots,
                    turnovers: raw.turnovers,
                    // always five boxes; more than five fouls still show all of them
                    fouls: fouls.length >= 5 ? fouls : [...fouls, ...marks(5 - fouls.length, '', false)],
                    // five fouls, or two towards a game disqualification
                    fouledOut: fouls.length >= 5 || fouls.filter(foul => foul.dq).length >= 2,
                    pir: raw.pir,
                    mvp: false
                }
            },
            teamColor(team, index) {
                const color = (team.color || team.brandColor || '').trim()
                // a light team color would vanish on the white sheet
                if (/^#?[0-9a-f]{6}$/i.test(color)) {
                    const hex = color.replace('#', '')
                    const light = (parseInt(hex.substr(0, 2), 16) * 299 + parseInt(hex.substr(2, 2), 16) * 587 + parseInt(hex.substr(4, 2), 16) * 114) / 1000
                    if (light < 200) return `#${hex}`
                }
                return PALETTE[index % PALETTE.length]
            },
            periodLabel(period) {
                return period <= 4 ? `${period}η` : `OT${period - 4}`
            },
            download() {
                const node = this.$refs.sheet
                if (!node) return
                this.saving = true
                toPng(node, { pixelRatio: 2, backgroundColor: '#ffffff', skipFonts: true, cacheBust: false })
                    .then(dataUrl => {
                        const [team1, team2] = this.sheet.teams
                        const slug = text => String(text).trim().replace(/[\s/\\:*?"<>|.]+/g, '-')
                        const link = document.createElement('a')
                        link.href = dataUrl
                        link.download = `fyllo-agona-${this.sheet.id}-${slug(team1.name)}-${slug(team2.name)}.png`
                        link.click()
                    })
                    .catch(() => {
                        this.error = 'Δεν ήταν δυνατή η δημιουργία της εικόνας.'
                    })
                    .finally(() => {
                        this.saving = false
                    })
            }
        }
    }
</script>

<style scoped>
.match-sheet-dialog__title {
    gap: 4px;
}

.match-sheet-dialog__body {
    padding: 16px !important;
    background: #eceff1;
    overflow-x: auto;
}

.match-sheet-dialog__state {
    display: flex;
    justify-content: center;
    padding: 48px 0;
}

/* ---------- the sheet itself: fixed width, so the image looks the same everywhere ---------- */
.match-sheet {
    width: 1120px;
    margin: 0 auto;
    padding: 28px 32px 24px;
    background: #fff;
    color: #232323;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 13px;
    line-height: 1.35;
}

.match-sheet__head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 14px;
    border-bottom: 4px solid #232323;
}

.match-sheet__logo {
    flex: none;
    width: auto;
    height: 64px;
}

.match-sheet__title {
    flex: 1;
    min-width: 0;
}

.match-sheet__kicker {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #03a985;
}

.match-sheet__competition {
    margin-top: 2px;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.01em;
}

.match-sheet__meta {
    color: #5f6368;
}

.match-sheet__no {
    padding: 6px 14px;
    border: 2px solid #232323;
    font-size: 11px;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.08em;
}

.match-sheet__no b {
    font-size: 18px;
    letter-spacing: 0;
}

/* ---------- the final score ---------- */
.match-sheet__score {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 18px 0 14px;
    background: #232323;
    color: #fff;
}

.match-sheet__side {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 20px;
}

.match-sheet__side--away {
    flex-direction: row-reverse;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.match-sheet__side--away .match-sheet__team {
    text-align: right;
}

.match-sheet__swatch {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 2px solid #fff;
    border-radius: 50%;
    font-size: 18px;
    font-weight: 800;
}

.match-sheet__badge {
    flex: 0 0 auto;
    width: 52px;
    height: 52px;
    padding: 4px;
    border-radius: 8px;
    background: #ffffff;
    object-fit: contain;
}

.match-sheet__team {
    flex: 1 1 auto;
    font-size: 19px;
    font-weight: 800;
    text-transform: uppercase;
}

.match-sheet__points {
    font-size: 44px;
    font-weight: 800;
    line-height: 1;
    opacity: 0.7;
}

.match-sheet__side--won .match-sheet__points {
    opacity: 1;
}

/* ---------- the tables ---------- */
.match-sheet__grid {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
}

.match-sheet__grid th,
.match-sheet__grid td {
    padding: 5px 6px;
    border: 1px solid #c9ced3;
    text-align: center;
    white-space: nowrap;
}

.match-sheet__grid th {
    background: #f1f3f4;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #3c4043;
}

.match-sheet__grid tbody tr:nth-child(even) td {
    background: #fafbfb;
}

.match-sheet__grid tfoot td {
    background: #f1f3f4;
    font-weight: 700;
}

.match-sheet__left {
    text-align: left !important;
}

.match-sheet__name {
    width: 32%;
}

.match-sheet__strong {
    font-weight: 800;
}

.match-sheet__grid th.match-sheet__strong {
    background: #232323;
    color: #fff;
}

.match-sheet__gap {
    width: 14px;
    border-top-color: transparent !important;
    border-bottom-color: transparent !important;
    background: #fff !important;
}

.match-sheet__periods td,
.match-sheet__periods th {
    padding: 6px 8px;
}

.match-sheet__dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 8px;
    border-radius: 50%;
    vertical-align: -1px;
}

.match-sheet__bonus {
    color: #d93025;
    font-weight: 800;
}

.match-sheet__box {
    margin-top: 18px;
}

.match-sheet__box-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: 6px 10px;
    border-left: 6px solid;
    background: #232323;
    color: #fff;
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
}

.match-sheet__coach {
    font-size: 12px;
    font-weight: 400;
    text-transform: none;
    color: rgba(255, 255, 255, 0.75);
}

.match-sheet__coach b {
    color: #fff;
}

.match-sheet__jersey {
    width: 34px;
    font-weight: 800;
}

.match-sheet__mvp {
    margin-left: 8px;
    padding: 1px 6px;
    border-radius: 3px;
    background: #03e5b7;
    color: #232323;
    font-size: 10px;
    font-weight: 800;
}

.match-sheet__fouls {
    display: inline-flex;
    gap: 2px;
}

.match-sheet__fouls i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border: 1px solid #c9ced3;
    font-size: 9px;
    font-style: normal;
    font-weight: 800;
}

.match-sheet__foul--on {
    border-color: #232323 !important;
    background: #232323;
    color: #fff;
}

/* a foul towards a game disqualification */
.match-sheet__foul--dq {
    border-radius: 50%;
}

.match-sheet__foul--out {
    border-color: #d93025 !important;
    background: #d93025;
}

.match-sheet__pcts td {
    font-weight: 400 !important;
    color: #5f6368;
}

.match-sheet__empty {
    padding: 14px !important;
    color: #80868b;
}

/* ---------- officials and signatures ---------- */
.match-sheet__foot {
    margin-top: 20px;
    padding-top: 14px;
    border-top: 4px solid #232323;
}

.match-sheet__officials {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 36px;
}

.match-sheet__label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #80868b;
}

.match-sheet__signs {
    display: flex;
    gap: 48px;
    margin-top: 26px;
}

.match-sheet__signs span {
    flex: 0 0 240px;
    padding-top: 6px;
    border-top: 1px solid #232323;
    font-size: 11px;
    color: #5f6368;
}

.match-sheet__legend {
    margin-top: 16px;
    font-size: 10px;
    color: #80868b;
}
</style>
